import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    CircularProgress,
    ClickAwayListener,
    createStyles,
    makeStyles,
    Theme,
    Typography,
    useMediaQuery,
    useTheme
} from '@material-ui/core';
import { wbp, reduceResolution } from 'Theme';
import ControlledPhoneField3 from 'components/UI/ControlledPhoneField3';
import { FieldError } from '@spike/model';
import { EmptyMessage } from 'model/ProspectMessage';
import { debounce, isEmpty, set } from 'lodash';
import { validatePhoneNumber } from '@spike/validations';
import { useMarketplace } from 'hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { Customer, PhoneSearchResult } from 'model/PhoneSearch';
import { useDispatch, useSelector } from 'react-redux';
import { PhoneSearchState, PhoneSearchStatus } from 'reducers/phoneSearch/phoneSearchState';
import { RootState } from 'store';
import { formatPhoneNumberByLength } from '../ProspectMessage/utils';
import { phoneSearchThunk } from 'actions/phoneSearch/phoneSearchActions';
import useNonInitialEffect from '@versiondos/hooks';
import { width } from '@fortawesome/pro-light-svg-icons/faTimes';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

interface Props {
    newMessage: EmptyMessage;
    errors: Array<FieldError>;
    setNewMessage: React.Dispatch<React.SetStateAction<EmptyMessage>>;
    handleClickOnPhone: (phoneSearchResult: PhoneSearchResult) => void;
    onBackButton: () => void;
    setErrors: React.Dispatch<Array<FieldError>>;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        chatHeaderContainer: {
            width: '100%',
            height: '8%',
            display: 'flex',
            position: 'relative',
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(5)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '5px'
            },

            [theme.breakpoints.up('lg')]: {
                alignItems: 'center',
                borderBottom: '#D4D4D4 1px solid',
                flexDirection: 'row',
                justifyContent: 'space-between'
            },

            [theme.breakpoints.down('md')]: {
                height: 'unset',
                paddingTop: '19px',
                flexDirection: 'column'
            }
        },
        chatHeaderLeftContainer: {
            display: 'flex',
            width: '50%',
            flexDirection: 'row',
            alignItems: 'center',

            [theme.breakpoints.down('md')]: {
                width: '100%'
            }
        },
        chatHeaderRightContainer: {
            display: 'flex',
            width: '50%',
            flexDirection: 'row-reverse',
            alignItems: 'center'
        },
        chatHeaderTitle: {
            color: '#000',
            fontWeight: 600,
            [theme.breakpoints.down(wbp)]: {
                paddingLeft: `${reduceResolution(25)}px !important`,
                fontSize: `${reduceResolution(16)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingLeft: '25px !important',
                fontSize: '16px'
            }
        },
        chatHeaderItem: {
            cursor: 'pointer',
            [theme.breakpoints.down(wbp)]: {
                paddingRight: `${reduceResolution(15)}px`,
                paddingLeft: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingRight: '15px',
                paddingLeft: '15px'
            }
        },
        chatBookNowButton: {
            [theme.breakpoints.down(wbp)]: {
                height: `${reduceResolution(37)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                height: '37px'
            }
        },
        inputLabel: {
            'width': 'unset',
            '& p': {
                fontSize: 14,
                fontWeight: 400
            },
            '& p:nth-of-type(2)': {
                display: 'none'
            }
        },
        input: {
            'flexDirection': 'row',
            'alignItems': 'center',
            '& input': {
                padding: '10px'
            },
            '& fieldset': {
                border: '0 !important'
            }
        },
        error: {
            'padding': '0 24px',
            '& p': {
                color: '#EF4F57',
                fontSize: 15,
                fontWeight: 500
            },

            [theme.breakpoints.down('md')]: {
                lineHeight: '0',
                marginTop: '-10px'
            }
        },
        backButton: {
            cursor: 'pointer',
            paddingLeft: '12px'
        },
        phoneResultsContainer: {
            backgroundColor: '#FFF',
            border: '1px solid #D3D3D3',
            borderRadius: '22px',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            padding: '15px',
            paddingBottom: '0',
            position: 'absolute',
            width: '350px',
            maxHeight: '280px',
            top: '65px',
            left: '56px',
            overflowY: 'scroll',
            overflowX: 'hidden',

            [theme.breakpoints.down('md')]: {
                width: '98%',
                left: '1%',
                top: '80px'
            }
        },
        phoneResultRow: {
            borderBottom: '1px solid #D3D3D3',
            paddingBottom: '15px',
            display: 'flex',
            alignItems: 'center'
        },
        phoneResult: {},
        phoneResultAvatar: {
            backgroundColor: ' #DCCCBB',
            marginRight: '8px'
        },
        phoneResultName: {
            fontSize: '16px',
            fontWeight: 500
        },
        phoneResultPhone: {
            fontSize: '14px',
            fontWeight: 400
        },
        circularLoading: {
            textAlign: 'center'
        }
    })
);

const NewPhoneChatHeader: FunctionComponent<Props> = props => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const marketplace = useMarketplace();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showResults, setShowResults] = useState(false);
    const [phoneSearchResults, setPhoneSearchResults] = useState<Array<PhoneSearchResult>>([]);

    const phoneSearchState = useSelector<RootState, PhoneSearchState>(state => state.phoneSearch);

    const debouncedSearch = useMemo(() => {
        return debounce((value: string) => {
            dispatch(phoneSearchThunk(value));
            setShowResults(true);
        }, 300);
    }, []);

    useEffect(() => {
        const phone = props.newMessage?.phone || '';

        if (phone.length >= 4) {
            debouncedSearch(phone);
        }

        return () => {
            debouncedSearch.cancel();
        };
    }, [props.newMessage?.phone]);

    useNonInitialEffect(() => {
        if (phoneSearchState.status === PhoneSearchStatus.SearchingSuccess) {
            setPhoneSearchResults(phoneSearchState.results);
        }
    }, [phoneSearchState.status]);

    const changeValueHandler = (value: any, fieldName?: string) => {
        if (fieldName && value) {
            props.setNewMessage(prev => {
                const updated = { ...(prev ?? { message: '', phone: '' }) };
                set(updated, fieldName, value);
                return updated;
            });
        }
    };

    const validate = () => {
        const errors: Array<FieldError> = [];

        props.newMessage?.phone &&
            !isEmpty(props.newMessage.phone) &&
            errors.push(
                ...validatePhoneNumber(props.newMessage?.phone, marketplace.basics.address.country!.id, 'phone')
            );

        return errors;
    };

    const handlePhone = () => {
        const errors = validate();
        props.setErrors(errors);
    };

    const clickAway = () => {
        setShowResults(false);
    };

    const searching = (
        <Box className={classes.circularLoading}>
            <CircularProgress size="1.5rem" />
        </Box>
    );

    const phoneResults = () => {
        return (
            <ClickAwayListener
                onClickAway={() => {
                    clickAway();
                }}
            >
                <OverlayScrollbarsComponent className={classes.phoneResultsContainer}>
                    {phoneSearchState.loading
                        ? searching
                        : phoneSearchResults.map(phoneResult => (
                              <Box
                                  key={`${phoneResult.type}-${phoneResult.id}`}
                                  className={classes.phoneResultRow}
                                  onClick={() => props.handleClickOnPhone(phoneResult)}
                              >
                                  <Avatar className={classes.phoneResultAvatar} />

                                  <Box>
                                      <Typography className={classes.phoneResultName}>
                                          {phoneResult.type === 'customer'
                                              ? `${(phoneResult.data as Customer).firstName} ${
                                                    (phoneResult.data as Customer).lastName
                                                }`
                                              : 'Unknown'}
                                      </Typography>
                                      <Typography className={classes.phoneResultPhone}>
                                          {formatPhoneNumberByLength(phoneResult.phone) ?? ''}
                                      </Typography>
                                  </Box>
                              </Box>
                          ))}
                </OverlayScrollbarsComponent>
            </ClickAwayListener>
        );
    };

    return (
        <Box className={classes.chatHeaderContainer}>
            <Box className={classes.chatHeaderLeftContainer}>
                {isMobile && (
                    <FontAwesomeIcon
                        icon={faArrowLeft}
                        size="lg"
                        onClick={() => props.onBackButton && props.onBackButton()}
                        className={classes.backButton}
                    />
                )}
                <ControlledPhoneField3
                    disabled={false}
                    className={classes.input}
                    labelClassName={classes.inputLabel}
                    label="To: "
                    placeholder="(___) ___-____"
                    name="phone"
                    autoComplete={'off'}
                    id="client_new_phone"
                    value={props.newMessage?.phone as string}
                    onChange={changeValueHandler}
                    required={true}
                    onBlur={handlePhone}
                />
            </Box>
            {props.errors.length > 0 && (
                <Box className={classes.error}>
                    <p>Please enter a valid phone number</p>
                </Box>
            )}

            {showResults && phoneSearchResults.length > 0 && phoneResults()}
        </Box>
    );
};

export default NewPhoneChatHeader;
